import { notifications } from '@mantine/notifications'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'

import { ConfirmationModal } from '@/components/ConfirmationModal/ConfirmationModal'
import type { UpdateAllTransactionsModalProps } from '@/components/UpdateAllTransactionsModal/UpdateAllTransactionsModal.types'

import {
  useBudgetServiceBudgetControllerGetAllBudgetsKey,
  useTransactionServiceTransactionControllerBulkEditTransactions,
  useTransactionServiceTransactionControllerGetTransactionsKey,
} from '@/__generated__/queries'

import { INSIGHTS_QUERY_KEY } from '@/hooks/useInsightsQuery.hook'

export function UpdateAllTransactionsModal({
  onClose,
  opened,
  otherTransactions,
}: UpdateAllTransactionsModalProps) {
  const queryClient = useQueryClient()

  const isMutating = useIsMutating({
    exact: true,
  })

  const INVALIDATE_KEYS = [
    useBudgetServiceBudgetControllerGetAllBudgetsKey,
    useTransactionServiceTransactionControllerGetTransactionsKey,
    INSIGHTS_QUERY_KEY,
  ]

  const updateAllTransactionsMutation =
    useTransactionServiceTransactionControllerBulkEditTransactions({
      onSuccess: () => {
        notifications.show({
          color: 'green',
          message: 'All transactions have been updated',
          title: 'Transactions have been updated',
        })

        INVALIDATE_KEYS.forEach(key => {
          queryClient.invalidateQueries({
            queryKey: [key],
          })
        })
      },
    })

  const handleUpdateAllTransactions = () => {
    if (!otherTransactions) return
    updateAllTransactionsMutation.mutate({ requestBody: otherTransactions })
    onClose()
  }

  const handleCloseModal = () => {
    INVALIDATE_KEYS.forEach(key => {
      queryClient.invalidateQueries({
        queryKey: [key],
      })
    })

    onClose()
  }

  return (
    <ConfirmationModal
      button={{ label: 'Update all', variant: 'primary' }}
      cancelLabel="Just this one"
      centered
      description="We noticed other transactions that look similar to this one. Would you like to apply the same changes to them as well?"
      isLoading={!!isMutating}
      onClose={handleCloseModal}
      onConfirm={handleUpdateAllTransactions}
      opened={opened}
      title="Apply changes to similar transactions?"
    />
  )
}

export default UpdateAllTransactionsModal
